import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactFlow, {
  Background,
  type Node,
  type Edge,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  ArrowLeft,
  Share2,
  Check,
  AlertTriangle,
  Lock,
  ChevronRight,
  Zap,
  BarChart3,
  Network,
  Lightbulb,
  Loader2,
  TrendingUp,
  Clock,
  DollarSign,
  Tag,
  Calendar,
} from 'lucide-react';
import clsx from 'clsx';
import type { AssessmentResults, AssessmentSession, ScorecardArea, Recommendation } from '../types';
import { getResults } from '../lib/api';
import EmailGate from '../components/EmailGate';

// ─── Mock data (shown when backend is unavailable) ────────────────

const MOCK_RESULTS: AssessmentResults = {
  executive_summary:
    'Your organization demonstrates a developing level of operational maturity with notable strengths in team collaboration and customer focus. Key opportunities exist in process automation, data infrastructure, and technology consolidation to unlock significant efficiency gains.',
  overall_score: 68,
  scorecard: {
    areas: [
      { name: 'Operations', score: 72, benchmark: 75, label: 'Developing', description: 'Core processes documented but inconsistently applied', color: 'yellow' },
      { name: 'Technology', score: 58, benchmark: 70, label: 'Emerging', description: 'Fragmented toolset with integration gaps', color: 'orange' },
      { name: 'People', score: 81, benchmark: 72, label: 'Proficient', description: 'Strong team culture and skills development', color: 'green' },
      { name: 'Data', score: 55, benchmark: 68, label: 'Emerging', description: 'Limited analytics and reporting capabilities', color: 'orange' },
      { name: 'Process', score: 74, benchmark: 76, label: 'Developing', description: 'Key workflows defined, automation opportunities exist', color: 'yellow' },
    ],
  },
  process_flow: {
    nodes: [
      { id: '1', type: 'custom', data: { label: 'Request Intake', description: 'Email & verbal requests', pain_point: true }, position: { x: 50, y: 100 } },
      { id: '2', type: 'custom', data: { label: 'Manual Triage', description: 'Team lead assigns tasks', pain_point: true }, position: { x: 250, y: 100 } },
      { id: '3', type: 'custom', data: { label: 'Task Execution', description: 'Work completed by team', pain_point: false }, position: { x: 450, y: 100 } },
      { id: '4', type: 'custom', data: { label: 'Review & QA', description: 'Manual review process', pain_point: false }, position: { x: 650, y: 100 } },
      { id: '5', type: 'custom', data: { label: 'Reporting', description: 'Spreadsheet-based reports', pain_point: true }, position: { x: 850, y: 100 } },
    ],
    edges: [
      { id: 'e1-2', source: '1', target: '2', animated: true },
      { id: 'e2-3', source: '2', target: '3' },
      { id: 'e3-4', source: '3', target: '4' },
      { id: 'e4-5', source: '4', target: '5', animated: true },
    ],
  },
  tech_landscape: {
    nodes: [
      { id: 't1', type: 'tech', data: { label: 'Email / Outlook', description: 'Communication', pain_point: false }, position: { x: 50, y: 50 } },
      { id: 't2', type: 'tech', data: { label: 'Excel / Sheets', description: 'Data & Reporting', pain_point: true }, position: { x: 250, y: 50 } },
      { id: 't3', type: 'tech', data: { label: 'Slack', description: 'Communication', pain_point: false }, position: { x: 50, y: 160 } },
      { id: 't4', type: 'tech', data: { label: 'No CRM', description: 'Gap: Customer Data', pain_point: true }, position: { x: 450, y: 50 } },
      { id: 't5', type: 'tech', data: { label: 'No Automation', description: 'Gap: Process Automation', pain_point: true }, position: { x: 450, y: 160 } },
      { id: 't6', type: 'tech', data: { label: 'Asana', description: 'Project Management', pain_point: false }, position: { x: 250, y: 160 } },
    ],
    edges: [
      { id: 'te1', source: 't1', target: 't6' },
      { id: 'te2', source: 't3', target: 't6' },
      { id: 'te3', source: 't6', target: 't2', animated: true, label: 'Manual export' },
    ],
  },
  recommendations: [
    { id: 'r1', priority: 1, title: 'Implement a CRM System', description: 'Deploy a CRM to centralize customer data, automate follow-ups, and provide real-time pipeline visibility. This will eliminate manual tracking and reduce response time by ~60%.', impact: 'High', effort: 'Medium', timeline: '60–90 days', roi_estimate: '3.2x ROI in 12 months', category: 'Technology' },
    { id: 'r2', priority: 2, title: 'Automate Request Intake', description: 'Replace email-based intake with a structured intake form connected to your project management tool. Reduce triage time from 2 hours/day to under 20 minutes.', impact: 'High', effort: 'Low', timeline: '2–3 weeks', roi_estimate: '~$42K saved annually', category: 'Process' },
    { id: 'r3', priority: 3, title: 'Build a Reporting Dashboard', description: 'Consolidate your spreadsheet-based reporting into a single live dashboard. Reduce weekly reporting prep from 4 hours to 15 minutes and improve decision-making speed.', impact: 'Medium', effort: 'Medium', timeline: '30–45 days', roi_estimate: '2.1x ROI in 6 months', category: 'Data' },
    { id: 'r4', priority: 4, title: 'Document Core SOPs', description: 'Formalize the top 5 recurring processes into Standard Operating Procedures. This will reduce onboarding time by 40% and improve consistency across the team.', impact: 'Medium', effort: 'Low', timeline: '3–4 weeks', roi_estimate: 'Reduces onboarding cost 40%', category: 'Operations' },
    { id: 'r5', priority: 5, title: 'Evaluate Integration Platform', description: 'Assess Zapier or Make.com to bridge your existing tools and eliminate manual data transfer. Target 3–5 high-frequency integrations as quick wins.', impact: 'High', effort: 'Low', timeline: '1–2 weeks', roi_estimate: '5+ hours saved/week', category: 'Technology' },
  ],
};

// ─── Score helpers ────────────────────────────────────────

function getScoreColors(score: number) {
  if (score < 50) return { text: 'text-red-400', border: 'border-red-500', bg: 'bg-red-500', ring: 'ring-red-500/30' };
  if (score < 65) return { text: 'text-orange-400', border: 'border-orange-500', bg: 'bg-orange-500', ring: 'ring-orange-500/30' };
  if (score < 75) return { text: 'text-yellow-400', border: 'border-yellow-500', bg: 'bg-yellow-500', ring: 'ring-yellow-500/30' };
  if (score < 85) return { text: 'text-green-400', border: 'border-green-500', bg: 'bg-green-500', ring: 'ring-green-500/30' };
  return { text: 'text-emerald-400', border: 'border-emerald-500', bg: 'bg-emerald-500', ring: 'ring-emerald-500/30' };
}

function getScoreLabel(score: number) {
  if (score < 50) return 'Beginning';
  if (score < 65) return 'Emerging';
  if (score < 75) return 'Developing';
  if (score < 85) return 'Proficient';
  return 'Advanced';
}

// ─── ReactFlow custom nodes ───────────────────────────────

function ProcessFlowNode({ data }: { data: { label: string; description: string; pain_point: boolean } }) {
  return (
    <div
      className={clsx(
        'px-4 py-3 rounded-xl border-2 text-sm min-w-[140px] shadow-[0_4px_29.8px_0_rgba(0,74,169,0.06)]',
        data.pain_point
          ? 'border-red-300 bg-red-50 text-red-700'
          : 'border-[#D8EBFE] bg-white text-[#333]'
      )}
    >
      <div className="font-semibold text-xs leading-snug">{data.label}</div>
      <div className="text-xs opacity-60 mt-1 leading-snug">{data.description}</div>
      {data.pain_point && (
        <div className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
          <AlertTriangle size={9} />
          <span>Pain Point</span>
        </div>
      )}
    </div>
  );
}

function TechNode({ data }: { data: { label: string; description?: string; category?: string; status?: string; pain_point?: boolean } }) {
  // Claude may return either `status: 'gap'` or `pain_point: true` to mark gaps
  const isGap = data.status === 'gap' || (data.pain_point && data.label.startsWith('No '));
  // Category can come from either `category` or `description` depending on the Claude response
  const categoryHint = (data.category || data.description || '').toLowerCase();
  const subtitle = data.description || data.category || '';
  return (
    <div
      className={clsx(
        'px-3 py-2.5 rounded-xl border-2 text-xs min-w-[120px]',
        isGap
          ? 'border-red-300 bg-red-50 text-red-600 border-dashed'
          : categoryHint.includes('communication')
          ? 'border-blue-300 bg-blue-50 text-blue-700'
          : categoryHint.includes('data')
          ? 'border-purple-300 bg-purple-50 text-purple-700'
          : categoryHint.includes('project') || categoryHint.includes('operations')
          ? 'border-green-300 bg-green-50 text-green-700'
          : 'border-[#D8EBFE] bg-[#F5FAFD] text-[#333]'
      )}
    >
      <div className="font-semibold">{data.label}</div>
      <div className="opacity-60 mt-0.5">{subtitle}</div>
      {isGap && (
        <div className="text-xs text-red-500 mt-1 flex items-center gap-1">
          <AlertTriangle size={8} /> Gap
        </div>
      )}
    </div>
  );
}

const nodeTypes = {
  custom: ProcessFlowNode,
  tech: TechNode,
};

// ─── Sub-components ───────────────────────────────────────

function ScoreCard({ score }: { score: number }) {
  const colors = getScoreColors(score);
  const label = getScoreLabel(score);
  return (
    <div
      className={clsx(
        'relative flex flex-col sm:flex-row items-center sm:items-start gap-6 p-6 rounded-[24px] bg-white border-2 shadow-[0_4px_29.8px_0_rgba(0,74,169,0.06)]',
        colors.border
      )}
    >
      {/* Score circle */}
      <div
        className={clsx(
          'relative w-28 h-28 rounded-full border-4 flex items-center justify-center flex-shrink-0 ring-8',
          colors.border,
          colors.ring
        )}
      >
        <div className="text-center">
          <span className={clsx('text-4xl font-black', colors.text)}>{score}</span>
          <span className="text-[#999] text-sm">/100</span>
        </div>
      </div>

      <div className="flex-1 text-center sm:text-left">
        <p className="text-[#999] text-xs uppercase tracking-widest font-semibold mb-1">
          Overall Maturity Score
        </p>
        <h2 className={clsx('text-3xl font-bold font-heading', colors.text)}>{label}</h2>
        <p className="text-[#666] text-sm mt-2 leading-relaxed max-w-lg">
          Your organization scores <strong className={colors.text}>{score}/100</strong> overall.
          The industry benchmark for your peer group is <strong className="text-[#333]">75</strong>.
        </p>

        {/* Mini progress */}
        <div className="mt-4 max-w-xs">
          <div className="flex justify-between text-xs text-[#999] mb-1">
            <span>0</span>
            <span className="text-[#666]">Benchmark: 75</span>
            <span>100</span>
          </div>
          <div className="relative h-2 bg-[#E8F4FC] rounded-full overflow-hidden">
            {/* Benchmark marker */}
            <div className="absolute top-0 bottom-0 w-0.5 bg-[#B8D4F0] z-10" style={{ left: '75%' }} />
            <motion.div
              className={clsx('h-full rounded-full', colors.bg)}
              initial={{ width: 0 }}
              animate={{ width: `${score}%` }}
              transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ScorecardAreaCard({ area }: { area: ScorecardArea }) {
  const colors = getScoreColors(area.score);
  return (
    <div className="p-4 rounded-xl bg-white border border-[#D8EBFE] hover:border-[#B8D4F0] transition-colors shadow-[0_4px_29.8px_0_rgba(0,74,169,0.06)]">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[#1A1A2E] text-sm font-semibold">{area.name}</span>
        <span className={clsx('text-lg font-bold', colors.text)}>{area.score}</span>
      </div>
      <p className="text-[#666] text-xs mb-3 leading-snug">{area.description}</p>
      <div className="relative h-1.5 bg-[#E8F4FC] rounded-full overflow-hidden">
        {/* Benchmark dot */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-[#B8D4F0] z-10"
          style={{ left: `${area.benchmark}%` }}
        />
        <motion.div
          className={clsx('h-full rounded-full', colors.bg)}
          initial={{ width: 0 }}
          animate={{ width: `${area.score}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
      <div className="flex justify-between text-xs mt-1.5">
        <span className={clsx('font-medium', colors.text)}>{area.label}</span>
        <span className="text-[#999]">Benchmark: {area.benchmark}</span>
      </div>
    </div>
  );
}

function RecommendationCard({
  rec,
  locked,
}: {
  rec: Recommendation;
  locked?: boolean;
}) {
  const impactColors = {
    High: 'text-green-400 bg-green-400/10 border-green-400/20',
    Medium: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    Low: 'text-slate-400 bg-slate-400/10 border-slate-400/20',
  };
  const effortColors = {
    High: 'text-red-400 bg-red-400/10 border-red-400/20',
    Medium: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    Low: 'text-green-400 bg-green-400/10 border-green-400/20',
  };

  return (
    <div
      className={clsx(
        'relative p-5 rounded-[24px] bg-white border border-[#D8EBFE] transition-all shadow-[0_4px_29.8px_0_rgba(0,74,169,0.06)]',
        locked && 'select-none'
      )}
    >
      {locked && (
        <div className="absolute inset-0 rounded-[24px] backdrop-blur-sm bg-white/60 z-10 flex items-center justify-center">
          <Lock size={20} className="text-[#999]" />
        </div>
      )}

      <div className="flex items-start gap-3 mb-3">
        <div className="w-7 h-7 rounded-lg bg-[#E8F4FC] border border-[#D8EBFE] flex items-center justify-center flex-shrink-0">
          <span className="text-[#0B3D91] text-xs font-bold">{rec.priority}</span>
        </div>
        <div className="flex-1">
          <h4 className="text-[#1A1A2E] font-semibold text-sm">{rec.title}</h4>
          <div className="flex items-center gap-1.5 mt-1">
            <Tag size={10} className="text-[#999]" />
            <span className="text-[#999] text-xs">{rec.category}</span>
          </div>
        </div>
      </div>

      <p className="text-[#666] text-sm leading-relaxed mb-4">{rec.description}</p>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="flex items-center gap-1.5 text-xs">
          <TrendingUp size={11} className="text-[#999]" />
          <span className="text-[#999]">Impact:</span>
          <span className={clsx('px-1.5 py-0.5 rounded border text-xs font-medium', impactColors[rec.impact])}>
            {rec.impact}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <Zap size={11} className="text-[#999]" />
          <span className="text-[#999]">Effort:</span>
          <span className={clsx('px-1.5 py-0.5 rounded border text-xs font-medium', effortColors[rec.effort])}>
            {rec.effort}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <Clock size={11} className="text-[#999]" />
          <span className="text-[#666]">{rec.timeline}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs">
          <DollarSign size={11} className="text-[#999]" />
          <span className="text-green-500 font-medium">{rec.roi_estimate}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Product Showcase ─────────────────────────────────────

const SHOWCASE_ITEMS = [
  {
    id: 'automation',
    icon: Zap,
    title: 'Real-time Process Automation',
    description: 'Trigger workflows automatically based on conditions, with no manual handoffs.',
    preview: (
      <div className="space-y-2 p-4 bg-[#F5FAFD] rounded-xl border border-[#D8EBFE] font-mono text-xs">
        <div className="text-[#999]">{'// Automation pipeline'}</div>
        <div><span className="text-[#0B3D91]">on</span><span className="text-[#333]">(</span><span className="text-green-600">'request.created'</span><span className="text-[#333]">)</span></div>
        <div className="pl-4"><span className="text-[#0C91C6]">.filter</span><span className="text-[#333]">{'(r => r.priority === '}</span><span className="text-orange-500">'high'</span><span className="text-[#333]">)</span></div>
        <div className="pl-4"><span className="text-[#0C91C6]">.assign</span><span className="text-[#333]">(</span><span className="text-green-600">'team-lead'</span><span className="text-[#333]">)</span></div>
        <div className="pl-4"><span className="text-[#0C91C6]">.notify</span><span className="text-[#333]">(</span><span className="text-green-600">'slack'</span><span className="text-[#333]">, </span><span className="text-green-600">'email'</span><span className="text-[#333]">)</span></div>
        <div className="pl-4"><span className="text-[#0C91C6]">.track</span><span className="text-[#333]">(</span><span className="text-green-600">'dashboard'</span><span className="text-[#333]">)</span></div>
        <div className="mt-3 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-green-600">12 triggers fired today</span>
        </div>
      </div>
    ),
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'AI-Powered Analytics',
    description: 'Surface insights from your data automatically. No SQL required.',
    preview: (
      <div className="p-4 bg-[#F5FAFD] rounded-xl border border-[#D8EBFE] space-y-3">
        <div className="text-xs text-[#666] font-medium">Weekly Performance</div>
        <div className="flex items-end gap-2 h-20">
          {[55, 70, 48, 82, 91, 78, 95].map((val, i) => (
            <motion.div
              key={i}
              className="flex-1 rounded-sm bg-[#0B3D91]/60"
              initial={{ height: 0 }}
              animate={{ height: `${val}%` }}
              transition={{ delay: i * 0.08, duration: 0.5, ease: 'easeOut' }}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-[#999]">
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <span key={i}>{d}</span>)}
        </div>
        <div className="flex gap-3 text-xs">
          <span className="text-green-500">↑ 23% vs last week</span>
          <span className="text-[#999]">Avg: 74</span>
        </div>
      </div>
    ),
  },
  {
    id: 'recommendations',
    icon: Lightbulb,
    title: 'Intelligent Recommendations',
    description: 'Continuously updated guidance based on your real-time data.',
    preview: (
      <div className="p-4 bg-[#F5FAFD] rounded-xl border border-[#D8EBFE] space-y-2">
        {[
          { text: 'Consolidate 3 overlapping tools', impact: 'High', color: 'text-green-500 bg-green-50 border-green-200' },
          { text: 'Automate weekly report generation', impact: 'High', color: 'text-green-500 bg-green-50 border-green-200' },
          { text: 'Add approval gate to intake process', impact: 'Med', color: 'text-yellow-500 bg-yellow-50 border-yellow-200' },
        ].map((item, i) => (
          <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white border border-[#D8EBFE]">
            <div className="flex items-center gap-2">
              <ChevronRight size={12} className="text-[#0C91C6]" />
              <span className="text-[#333] text-xs">{item.text}</span>
            </div>
            <span className={clsx('text-xs px-1.5 py-0.5 rounded border font-medium', item.color)}>
              {item.impact}
            </span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'integrations',
    icon: Network,
    title: 'One-Click Integrations',
    description: 'Connect your existing stack in minutes, with 200+ integrations available.',
    preview: (
      <div className="p-4 bg-[#F5FAFD] rounded-xl border border-[#D8EBFE]">
        <div className="grid grid-cols-3 gap-2">
          {[
            { name: 'Slack', color: 'border-purple-200 bg-purple-50 text-purple-600', connected: true },
            { name: 'HubSpot', color: 'border-orange-200 bg-orange-50 text-orange-600', connected: true },
            { name: 'Asana', color: 'border-red-200 bg-red-50 text-red-600', connected: true },
            { name: 'Salesforce', color: 'border-blue-200 bg-blue-50 text-blue-600', connected: false },
            { name: 'Notion', color: 'border-[#D8EBFE] bg-white text-[#333]', connected: false },
            { name: '+ 195 more', color: 'border-[#D8EBFE] bg-[#E8F4FC] text-[#0B3D91]', connected: false },
          ].map((item, i) => (
            <div key={i} className={clsx('px-2 py-2 rounded-lg border text-xs text-center font-medium', item.color)}>
              {item.name}
              {item.connected && (
                <div className="flex items-center justify-center mt-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    ),
  },
];

function ProductShowcase() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((i) => (i + 1) % SHOWCASE_ITEMS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const item = SHOWCASE_ITEMS[activeIndex];
  const Icon = item.icon;

  return (
    <div className="rounded-[24px] bg-white border border-[#D8EBFE] overflow-hidden shadow-[0_4px_29.8px_0_rgba(0,74,169,0.06)]">
      {/* Tabs */}
      <div className="flex border-b border-[#D8EBFE] overflow-x-auto">
        {SHOWCASE_ITEMS.map((s, i) => {
          const SIcon = s.icon;
          return (
            <button
              key={s.id}
              onClick={() => setActiveIndex(i)}
              className={clsx(
                'flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0',
                activeIndex === i
                  ? 'text-[#0B3D91] border-b-2 border-[#0B3D91] bg-[#F5FAFD]'
                  : 'text-[#999] hover:text-[#333]'
              )}
            >
              <SIcon size={14} />
              {s.title.split(' ').slice(0, 2).join(' ')}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeIndex}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.3 }}
          className="p-6 grid md:grid-cols-2 gap-6"
        >
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-[#E8F4FC] border border-[#D8EBFE] flex items-center justify-center">
                <Icon size={18} className="text-[#0B3D91]" />
              </div>
              <h3 className="text-[#1A1A2E] font-semibold">{item.title}</h3>
            </div>
            <p className="text-[#666] text-sm leading-relaxed">{item.description}</p>

            {/* Dot indicators */}
            <div className="flex gap-1.5 mt-4">
              {SHOWCASE_ITEMS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={clsx(
                    'h-1.5 rounded-full transition-all duration-300',
                    i === activeIndex ? 'w-6 bg-[#0B3D91]' : 'w-1.5 bg-[#D8EBFE]'
                  )}
                />
              ))}
            </div>
          </div>
          <div>{item.preview}</div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────

export default function ResultsPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<AssessmentSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [emailUnlocked, setEmailUnlocked] = useState(false);
  const [calUrl, setCalUrl] = useState<string>(import.meta.env.VITE_BOOKING_URL || '');
  const [showEmailGate, setShowEmailGate] = useState(false);

  const results: AssessmentResults = session?.results_data ?? MOCK_RESULTS;

  const loadResults = useCallback(async () => {
    if (!token) return;
    try {
      const data = await getResults(token);
      setSession(data);
      if (data.contact_email) setEmailUnlocked(true);
    } catch {
      // Backend unavailable — show mock data
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  function handleShare() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleEmailSuccess(url: string) {
    setEmailUnlocked(true);
    setCalUrl(url || import.meta.env.VITE_BOOKING_URL || 'https://calendar.app.google/6ttxLKiKzDmwaj4a7');
    setShowEmailGate(false);
  }

  const processNodes: Node[] = results.process_flow.nodes.map((n) => ({
    id: n.id,
    type: 'custom',
    data: n.data,
    position: n.position,
  }));

  const processEdges: Edge[] = results.process_flow.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    animated: e.animated,
    label: e.label,
    style: { stroke: '#B8D4F0', strokeWidth: 1.5 },
    labelStyle: { fill: '#666', fontSize: 10 },
    labelBgStyle: { fill: '#F5FAFD', fillOpacity: 0.9 },
  }));

  const techNodes: Node[] = results.tech_landscape.nodes.map((n) => ({
    id: n.id,
    type: 'tech',
    data: n.data,
    position: n.position,
  }));

  const techEdges: Edge[] = results.tech_landscape.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    animated: e.animated,
    label: e.label,
    style: { stroke: '#B8D4F0', strokeWidth: 1.5 },
    labelStyle: { fill: '#666', fontSize: 10 },
    labelBgStyle: { fill: '#F5FAFD', fillOpacity: 0.9 },
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={32} className="text-[#0B3D91] animate-spin" />
          <p className="text-[#666] text-sm">Loading your results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#333]">
      {/* Email Gate Modal */}
      <AnimatePresence>
        {showEmailGate && token && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && setShowEmailGate(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              className="w-full max-w-md"
            >
              <EmailGate sessionToken={token} onSuccess={handleEmailSuccess} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-40 border-b border-[#D8EBFE] bg-white/90 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#666] hover:text-[#0B3D91] transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            Home
          </button>

          <div className="text-center">
            <h1 className="text-base font-semibold font-heading text-[#004AA9]">Your Assessment Results</h1>
            {session?.company_name && (
              <p className="text-xs text-[#999] mt-0.5">Assessment for {session.company_name}</p>
            )}
          </div>

          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#F5FAFD] hover:bg-[#E8F4FC] border border-[#D8EBFE] text-[#333] hover:text-[#0B3D91] transition-all text-sm"
          >
            {copied ? <Check size={14} className="text-green-500" /> : <Share2 size={14} />}
            {copied ? 'Copied!' : 'Share'}
          </button>
        </div>
      </header>

      {/* ── Body ───────────────────────────────────────── */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-12">

        {/* ── Executive Summary ─────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="p-5 rounded-[24px] bg-gradient-to-r from-[#D8EBFE] to-[#ECF6FF] border border-[#D8EBFE]">
            <p className="text-xs uppercase tracking-widest text-[#0B3D91] font-semibold mb-2">
              Executive Summary
            </p>
            <p className="text-[#333] leading-relaxed">{results.executive_summary}</p>
          </div>
        </motion.section>

        {/* ── Overall Score ─────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="space-y-4"
        >
          <ScoreCard score={results.overall_score} />

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {results.scorecard.areas.map((area) => (
              <ScorecardAreaCard key={area.name} area={area} />
            ))}
          </div>
        </motion.section>

        {/* ── Process Flow ──────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-3"
        >
          <div>
            <h2 className="text-xl font-bold font-heading text-[#004AA9]">Current Process Flow</h2>
            <p className="text-[#666] text-sm mt-1">
              Visual map of your workflow with identified pain points.
            </p>
          </div>

          <div className="rounded-[24px] border border-[#D8EBFE] overflow-hidden" style={{ height: 280 }}>
            <ReactFlow
              nodes={processNodes}
              edges={processEdges}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag={false}
              zoomOnScroll={false}
              zoomOnPinch={false}
              preventScrolling={false}
              attributionPosition="bottom-right"
            >
              <Background
                variant={BackgroundVariant.Dots}
                gap={24}
                size={1}
                color="#D8EBFE"
              />
            </ReactFlow>
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-[#666]">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded border-2 border-[#D8EBFE] bg-white" />
              Standard step
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded border-2 border-red-300 bg-red-50" />
              Pain point identified
            </div>
          </div>
        </motion.section>

        {/* ── Technology Landscape ──────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="space-y-3"
        >
          <div>
            <h2 className="text-xl font-bold font-heading text-[#004AA9]">Technology Landscape</h2>
            <p className="text-[#666] text-sm mt-1">
              Your current tech stack and identified capability gaps.
            </p>
          </div>

          <div className="rounded-[24px] border border-[#D8EBFE] overflow-hidden" style={{ height: 280 }}>
            <ReactFlow
              nodes={techNodes}
              edges={techEdges}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag={false}
              zoomOnScroll={false}
              zoomOnPinch={false}
              preventScrolling={false}
              attributionPosition="bottom-right"
            >
              <Background
                variant={BackgroundVariant.Dots}
                gap={24}
                size={1}
                color="#D8EBFE"
              />
            </ReactFlow>
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-[#666]">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded border-2 border-blue-300" />
              Communication
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded border-2 border-green-300" />
              Operations
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded border-2 border-purple-300" />
              Data
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded border-2 border-red-300 border-dashed" />
              Capability gap
            </div>
          </div>
        </motion.section>

        {/* ── Recommendations ───────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="space-y-4"
        >
          <div>
            <h2 className="text-xl font-bold font-heading text-[#004AA9]">Recommendations</h2>
            <p className="text-[#666] text-sm mt-1">
              Prioritized actions to accelerate your transformation.
            </p>
          </div>

          {!emailUnlocked ? (
            <div className="relative">
              {/* Blurred preview cards */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pointer-events-none select-none">
                {results.recommendations.slice(0, 3).map((rec) => (
                  <RecommendationCard key={rec.id} rec={rec} locked />
                ))}
              </div>

              {/* Unlock overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="relative z-10 w-full max-w-md mx-auto p-6 rounded-[24px] border border-[#0B3D91]/30 text-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(11, 61, 145, 0.95), rgba(0, 74, 169, 0.95))',
                    boxShadow: '0 0 40px rgba(11, 61, 145, 0.2)',
                  }}
                >
                  <div className="w-12 h-12 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center mx-auto mb-4">
                    <Lock size={22} className="text-white" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">
                    Unlock Your Full Recommendations
                  </h3>
                  <p className="text-white/70 text-sm mb-5 leading-relaxed">
                    Get specific, actionable steps with projected ROI tailored to your situation.
                    Free, takes 30 seconds.
                  </p>
                  <button
                    onClick={() => setShowEmailGate(true)}
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-[#0B3D91] bg-white transition-all hover:scale-[1.02]"
                  >
                    Enter your email to unlock
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {results.recommendations.map((rec) => (
                <RecommendationCard key={rec.id} rec={rec} />
              ))}
            </div>
          )}
        </motion.section>

        {/* ── Product Showcase ──────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="space-y-4"
        >
          <div>
            <h2 className="text-xl font-bold font-heading text-[#004AA9]">See What's Possible</h2>
            <p className="text-[#666] text-sm mt-1">
              Here's how we'd help you implement these recommendations.
            </p>
          </div>
          <ProductShowcase />
        </motion.section>

        {/* ── Final CTA ─────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          {emailUnlocked && calUrl ? (
            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-2xl font-bold font-heading text-[#004AA9] mb-2">Book Your Strategy Session</h2>
                <p className="text-[#666] text-sm">
                  Let's walk through your results and build a custom roadmap together.
                </p>
              </div>
              {/* Calendly inline widget */}
              <div
                className="calendly-inline-widget rounded-[24px] border border-[#D8EBFE] overflow-hidden"
                data-url={calUrl}
                style={{ minWidth: '320px', height: '700px' }}
              />
              {/* Fallback popup button */}
              <div className="text-center">
                <button
                  onClick={() => {
                    if (typeof window !== 'undefined' && (window as any).Calendly) {
                      (window as any).Calendly.initPopupWidget({ url: calUrl });
                    } else {
                      window.open(calUrl, '_blank', 'noopener');
                    }
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, #0B3D91, #004AA9)' }}
                >
                  <Calendar size={18} />
                  Schedule Appointment
                </button>
                <p className="text-[#999] text-xs mt-2">
                  Free · No commitment · 30 minutes
                </p>
              </div>
            </div>
          ) : (
            <div
              className="rounded-[24px] p-8 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(11, 61, 145, 0.95), rgba(0, 74, 169, 0.95))',
                border: '1px solid rgba(11, 61, 145, 0.3)',
              }}
            >
              <h2 className="text-2xl font-bold font-heading text-white mb-3">
                Ready to see how this applies to you?
              </h2>
              <p className="text-white/70 mb-6 max-w-md mx-auto text-sm leading-relaxed">
                Book a free 30-minute strategy session. We'll review your results and map out a
                concrete implementation plan.
              </p>
              <button
                onClick={() => setShowEmailGate(true)}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-semibold text-[#0B3D91] bg-white transition-all hover:scale-[1.03]"
              >
                Unlock Results & Book a Call
                <ChevronRight size={16} />
              </button>
              <p className="text-white/50 text-xs mt-3">
                Free · No commitment · 30 minutes
              </p>
            </div>
          )}
        </motion.section>
      </main>

      {/* ── Footer ─────────────────────────────────────── */}
    </div>
  );
}
